import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import SignaturePad from "@/components/SignaturePad";
import { Sparkle, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoSign = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const padRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/cosign/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This link is invalid or expired."));
  }, [token]);

  const submit = async () => {
    const img = padRef.current?.getDataURL();
    if (!name.trim()) { setError("Please enter your name."); return; }
    if (!img) { setError("Please add your signature."); return; }
    setSubmitting(true); setError("");
    try {
      await axios.post(`${API}/cosign/${token}`, { name, image_base64: img });
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not submit signature.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-lg surface rounded-[2rem] p-8 animate-fade-up">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
            <Sparkle className="w-5 h-5 text-white" />
          </div>
          <span className="gifted-brand text-2xl">GIFTED</span>
        </div>

        {error && !info && <div data-testid="cosign-error" className="text-center text-red-500">{error}</div>}

        {done ? (
          <div className="text-center py-8" data-testid="cosign-done">
            <div className="w-14 h-14 rounded-full bg-secondary/15 flex items-center justify-center mx-auto mb-4">
              <Check className="w-7 h-7 text-secondary" />
            </div>
            <h2 className="font-serif text-2xl mb-2">Thank you!</h2>
            <p className="text-muted-foreground">Your signature has been added to the card. It means the world.</p>
          </div>
        ) : info ? (
          <>
            <p className="text-center text-muted-foreground mb-1">
              <span className="text-foreground font-medium">{info.requested_by}</span> invited you to co-sign a
            </p>
            <h2 className="font-serif text-3xl text-center mb-5">{info.occasion} card for {info.loved_one_name}</h2>
            {info.image_base64 && (
              <div className="rounded-2xl overflow-hidden border border-border mb-4">
                <img src={`data:image/png;base64,${info.image_base64}`} alt="" className="w-full h-48 object-cover" />
              </div>
            )}
            {info.message && <p className="font-serif italic text-center text-muted-foreground mb-5">“{info.message}”</p>}

            <label className="text-sm text-muted-foreground">Your name</label>
            <input data-testid="cosign-name" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full mt-1 mb-4 bg-input border border-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="e.g. Alex" />

            <label className="text-sm text-muted-foreground">Your signature</label>
            <div className="mt-1 mb-3"><SignaturePad ref={padRef} label="Sign with your finger or stylus" /></div>

            {error && <div data-testid="cosign-error" className="text-sm text-red-500 mb-3">{error}</div>}

            <button data-testid="cosign-submit" onClick={submit} disabled={submitting}
              className="w-full bg-gradient-to-r from-primary to-secondary text-white rounded-2xl py-3.5 hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding…</> : "Add My Signature"}
            </button>
          </>
        ) : (
          <div className="text-center py-10 text-muted-foreground flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
        )}
      </div>
    </div>
  );
};

export default CoSign;
